import React, { useEffect, useState, ReactElement, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';

import Theme from '@styles/Theme';
import { cardCommonStyle } from '@styles/common/Card';

const ItemBottomEnter = keyframes`
  0%{
    transform: translateY(0);
    opacity: 0;
  }
  100%{
    transform: translateY(-15%);
    opacity: 1;
  }
`;

const ItemBottomExit = keyframes`
  0%{
    transform: translateY(-15%);
    opacity: 1;
  }
  100%{
    transform: translateY(0);
    opacity: 0;
  }
`;

const ItemRightEnter = keyframes`
  0%{
    transform: translateX(15%);
    opacity: 0;
  }
  100%{
    transform: translateX(0);
    opacity: 1;
  }
`;

const ItemRightExit = keyframes`
  0%{
    transform: translateX(0);
    opacity: 1;
  }
  100%{
    transform: translateX(15%);
    opacity: 0;
  }
`;

const dirMap = {
  bottom: css`
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
  `,
  right: css`
    right: 60px;
    top: 80px;
  `,
  leftBottom: css`
    bottom: 30px;
    left: 30px;
  `,
  rightBottom: css`
    bottom: 30px;
    right: 30px;
  `,
  leftTop: css`
    top: 80px;
    left: 60px;
  `,
  rightTop: css`
    top: 80px;
    right: 60px;
  `,
};

export type SnackBarDirection = 'bottom' | 'right' | 'leftBottom' | 'rightBottom' | 'leftTop' | 'rightTop';

const SnackBarSec = styled.div<{
  dir: SnackBarDirection;
}>`
  position: fixed;
  display: flex;
  justify-content: center;
  z-index: 4;
  ${({ dir }) => dirMap[dir]}

  @media (max-width: 480px) {
    width: 100%;
    top: 60px;
    left: 0;
    transform: none;
  }
`;

const SnackBarWrap = styled.div<{
  isShow: boolean;
  dir: SnackBarDirection;
}>`
  ${cardCommonStyle};
  box-shadow: ${Theme.shadow.dropdown};
  padding: 16px;
  text-align: center;

  transition: all 1s ease-in-out;
  transform-origin: top center;
  animation: 0.6s ${Theme.animation.modal_cubic_bezier} both alternate
    ${({ isShow, dir }) => {
      const isBottomDir = dir === 'bottom' || dir === 'leftBottom' || dir === 'rightBottom';
      return isShow
        ? (isBottomDir ? ItemBottomEnter : ItemRightEnter)
        : (isBottomDir ? ItemBottomExit : ItemRightExit);
    }};
  overflow: visible;

  @media (max-width: 480px) {
    width: 90%;
  }
`;

interface SnackBarProps {
  children: ReactElement;
  show: boolean;
  setShow: any;
  removeDelay?: number;
  noDelay?: boolean;
  dir?: SnackBarDirection;
}

function SnackBar({
  children,
  show,
  setShow,
  removeDelay = 100,
  noDelay,
  dir = 'right',
}: SnackBarProps) {
  const [shouldRender, setRender] = useState(show);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const unmountedRef = useRef(false);

  useEffect(() => {
    unmountedRef.current = false;

    if (show) {
      setRender(true);

      if (!noDelay) {
        timerRef.current = setTimeout(() => {
          if (!unmountedRef.current) {
            setShow(false);
          }
        }, removeDelay);
      }
    }

    return () => {
      unmountedRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [show]);

  const onAnimationEnd = (e) => {
    e.preventDefault();
    if (!show) {
      setRender(false);
    }
  };

  if (!shouldRender) return null;

  return (
    <SnackBarSec dir={dir}>
      <SnackBarWrap dir={dir} isShow={show} onAnimationEnd={onAnimationEnd}>
        {children}
      </SnackBarWrap>
    </SnackBarSec>
  );
}

export default SnackBar;
